import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSEO from "@/components/feature/PageSEO";
import { categories } from "@/mocks/comics";
import { hotComics, dailyUpdates } from "@/mocks/comics";
import { siteInfo } from "@/mocks/siteInfo";

export default function CategoryDetail() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [sortBy, setSortBy] = useState<"latest" | "popular" | "rating">("latest");

  const allCategoryComics = [...hotComics, ...dailyUpdates];
  const category = categories.find((c) => c.id === slug);
  const categoryName = category?.name || "全部";
  const categoryComics = category
    ? allCategoryComics.filter((c) => c.category === category.name)
    : allCategoryComics;

  const sortedComics = [...categoryComics].sort((a, b) => {
    if (sortBy === "latest") return (b.id || 0) - (a.id || 0);
    if (sortBy === "popular") return b.id - a.id;
    return 0;
  });

  const breadcrumbItems = [
    { label: t("nav.category"), path: "/category" },
    { label: categoryName },
  ];

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: siteInfo.name + " - " + categoryName + "分类",
      description: categoryName + "分类韩漫大全，精选" + categoryComics.length + "部优质作品，免费在线阅读。",
      url: siteInfo.url + "/category/" + slug,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t("breadcrumb.home"), item: siteInfo.url },
        { "@type": "ListItem", position: 2, name: t("nav.category"), item: siteInfo.url + "/category" },
        { "@type": "ListItem", position: 3, name: categoryName },
      ],
    },
  ];

  return (
    <>
      <PageSEO
        title={siteInfo.name + " - " + categoryName + "分类大全"}
        description={categoryName + "分类韩漫大全，精选" + categoryComics.length + "部优质作品，每日更新，免费在线阅读。恋爱漫画、奇幻漫画、动作漫画、悬疑漫画、耽美漫画等海量资源。"}
        keywords={categoryName + "漫画," + categoryName + "韩漫,韩国" + categoryName + "漫画,免费" + categoryName + "漫画"}
        canonicalUrl={siteInfo.url + "/category/" + slug}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-4">
                  <i className={category?.icon || "ri-folder-line" + " text-sm w-4 h-4 flex items-center justify-center"}></i>
                  共 {categoryComics.length} 部作品
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950">
                  {categoryName}
                </h1>
                <p className="mt-3 text-sm text-foreground-600 max-w-xl mx-auto">
                  精选{categoryName}分类优质韩漫作品，画质高清，翻译精准，免费阅读。
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={"/category/" + cat.id}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                        cat.id === slug
                          ? "bg-primary-500 text-white"
                          : "bg-white text-foreground-600 border border-background-200 hover:border-primary-300"
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400">排序：</span>
                  {([
                    { key: "latest", label: "最新" },
                    { key: "popular", label: "热门" },
                    { key: "rating", label: "评分" },
                  ] as { key: "latest" | "popular" | "rating"; label: string }[]).map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        sortBy === opt.key
                          ? "bg-foreground-900 text-white"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {sortedComics.map((comic) => (
                  <div key={comic.id} className="group cursor-pointer">
                    <div className="relative rounded-2xl overflow-hidden aspect-[3/4] mb-3">
                      <img
                        src={comic.imageUrl}
                        alt={comic.title}
                        title={comic.title + " - 在线漫画韩漫"}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <p className="text-xs text-white leading-relaxed line-clamp-2">{(comic as { desc?: string }).desc || "暂无简介"}</p>
                      </div>
                    </div>
                    <span className="inline-block px-2 py-0.5 rounded-md bg-accent-500 text-white text-xs font-medium mb-1.5">
                      {comic.category}
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2">
                      {comic.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{comic.author}</p>
                  </div>
                ))}
              </div>

              {sortedComics.length === 0 && (
                <div className="text-center py-16">
                  <i className="ri-inbox-line text-4xl text-foreground-300 w-10 h-10 flex items-center justify-center mx-auto"></i>
                  <p className="mt-4 text-foreground-500">该分类下暂无作品，去看看其他分类吧~</p>
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                    返回分类总览
                  </Link>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}